import type { IBrand, IMarket } from 'data/schema';

/**
 * Multilingual market name aliases.
 * Market code itself is implicitly searchable (added by the search engine).
 * Languages: English, German, French, Spanish, + native name where different.
 */
export const marketAliases: Record<IMarket, string[]> = {
  ARE: [
    'United Arab Emirates',
    'Vereinigte Arabische Emirate',
    'Émirats arabes unis',
    'Emiratos Árabes Unidos',
    'الإمارات العربية المتحدة',
  ],
  ARG: ['Argentina', 'Argentinien', 'Argentine'],
  AUS: ['Australia', 'Australien', 'Australie'],
  AUT: ['Austria', 'Österreich', 'Autriche'],
  BEL: ['Belgium', 'Belgien', 'Belgique', 'Bélgica', 'België'],
  BHR: ['Bahrain', 'Bahreïn', 'Baréin', 'البحرين'],
  BOL: ['Bolivia', 'Bolivien', 'Bolivie'],
  BIH: [
    'Bosnia and Herzegovina',
    'Bosnien und Herzegowina',
    'Bosnie-Herzégovine',
    'Bosnia y Herzegovina',
    'Bosna i Hercegovina',
  ],
  BRA: ['Brazil', 'Brasilien', 'Brésil', 'Brasil'],
  CAN: ['Canada', 'Kanada'],
  CHE: ['Switzerland', 'Schweiz', 'Suisse', 'Suiza', 'Svizzera'],
  CHL: ['Chile', 'Chili'],
  CHN: ['China', 'Chine', '中国'],
  COL: ['Colombia', 'Kolumbien', 'Colombie'],
  CRI: ['Costa Rica'],
  CYP: ['Cyprus', 'Zypern', 'Chypre', 'Chipre', 'Κύπρος'],
  CYM: ['Cayman Islands', 'Kaimaninseln', 'Îles Caïmans', 'Islas Caimán'],
  CUW: ['Curaçao', 'Curacao'],
  DEU: ['Germany', 'Deutschland', 'Allemagne', 'Alemania'],
  DNK: ['Denmark', 'Dänemark', 'Danemark', 'Dinamarca', 'Danmark'],
  DOM: ['Dominican Republic', 'Dominikanische Republik', 'République dominicaine', 'República Dominicana'],
  ECU: ['Ecuador', 'Équateur'],
  EGY: ['Egypt', 'Ägypten', 'Égypte', 'Egipto', 'مصر'],
  ESP: ['Spain', 'Spanien', 'Espagne', 'España'],
  EST: ['Estonia', 'Estland', 'Estonie', 'Eesti'],
  FIN: ['Finland', 'Finnland', 'Finlande', 'Finlandia', 'Suomi'],
  FRA: ['France', 'Frankreich', 'Francia'],
  GBR: ['United Kingdom', 'Vereinigtes Königreich', 'Royaume-Uni', 'Reino Unido', 'Great Britain', 'UK', 'England'],
  GLP: ['Guadeloupe', 'Guadalupe'],
  GRC: ['Greece', 'Griechenland', 'Grèce', 'Grecia', 'Ελλάδα'],
  GTM: ['Guatemala'],
  GUF: ['French Guiana', 'Französisch-Guayana', 'Guyane française', 'Guayana Francesa'],
  IRL: ['Ireland', 'Irland', 'Irlande', 'Irlanda', 'Éire'],
  ITA: ['Italy', 'Italien', 'Italie', 'Italia'],
  IND: ['India', 'Indien', 'Inde', 'भारत'],
  JAM: ['Jamaica', 'Jamaika', 'Jamaïque'],
  JPN: ['Japan', 'Japon', 'Japón', '日本'],
  KOR: ['South Korea', 'Südkorea', 'Corée du Sud', 'Corea del Sur', '대한민국'],
  KWT: ['Kuwait', 'Koweït', 'الكويت'],
  LBN: ['Lebanon', 'Libanon', 'Liban', 'Líbano', 'لبنان'],
  LCA: ['Saint Lucia', 'St. Lucia', 'Sainte-Lucie', 'Santa Lucía'],
  LUX: ['Luxembourg', 'Luxemburg', 'Luxemburgo', 'Lëtzebuerg'],
  LVA: ['Latvia', 'Lettland', 'Lettonie', 'Letonia', 'Latvija'],
  LTU: ['Lithuania', 'Litauen', 'Lituanie', 'Lituania', 'Lietuva'],
  MEX: ['Mexico', 'Mexiko', 'Mexique', 'México'],
  MTQ: ['Martinique', 'Martinica'],
  NLD: ['Netherlands', 'Niederlande', 'Pays-Bas', 'Países Bajos', 'Holland', 'Nederland'],
  NOR: ['Norway', 'Norwegen', 'Norvège', 'Noruega', 'Norge'],
  NZL: ['New Zealand', 'Neuseeland', 'Nouvelle-Zélande', 'Nueva Zelanda', 'Aotearoa'],
  OMN: ['Oman', 'عُمان'],
  PAN: ['Panama', 'Panamá'],
  PER: ['Peru', 'Pérou', 'Perú'],
  POL: ['Poland', 'Polen', 'Pologne', 'Polonia', 'Polska'],
  PRY: ['Paraguay'],
  QAT: ['Qatar', 'Katar', 'قطر'],
  ROU: ['Romania', 'Rumänien', 'Roumanie', 'Rumania', 'România'],
  SAU: ['Saudi Arabia', 'Saudi-Arabien', 'Arabie saoudite', 'Arabia Saudita', 'المملكة العربية السعودية'],
  SGP: ['Singapore', 'Singapur', 'Singapour', '新加坡'],
  SLV: ['El Salvador'],
  SRB: ['Serbia', 'Serbien', 'Serbie', 'Србија', 'Srbija'],
  SVK: ['Slovakia', 'Slowakei', 'Slovaquie', 'Eslovaquia', 'Slovensko'],
  SWE: ['Sweden', 'Schweden', 'Suède', 'Suecia', 'Sverige'],
  SXM: ['Sint Maarten', 'Saint-Martin', 'San Martín'],
  TTO: ['Trinidad and Tobago', 'Trinidad und Tobago', 'Trinité-et-Tobago', 'Trinidad y Tobago'],
  TUR: ['Turkey', 'Türkei', 'Turquie', 'Turquía', 'Türkiye'],
  TWN: ['Taiwan', 'Taïwan', 'Taiwán', '台灣'],
  URY: ['Uruguay'],
  USA: ['United States', 'United States of America', 'Vereinigte Staaten', 'États-Unis', 'Estados Unidos', 'America'],
  ZAF: ['South Africa', 'Südafrika', 'Afrique du Sud', 'Sudáfrica'],
  DEV: ['Development', 'Dev', 'Entwicklung', 'Développement', 'Desarrollo'],
  '*': ['All Markets', 'Alle Märkte', 'Tous les marchés', 'Todos los mercados'],
};

/**
 * Brand name aliases.
 * Brand code itself is implicitly searchable (added by the search engine).
 */
export const brandAliases: Record<IBrand, string[]> = {
  A: ['Audi'],
  V: ['Volkswagen', 'VW'],
  S: ['Cupra', 'Seat'],
  C: ['Skoda', 'Škoda'],
  E: ['Bentley'],
  L: ['Lamborghini', 'Lambo'],
  M: ['MAN', 'MAN Trucks'],
  N: ['VW Nutzfahrzeuge', 'Volkswagen Commercial', 'VWN'],
  P: ['Porsche'],
  '*': ['All Brands', 'Alle Marken', 'Toutes les marques', 'Todas las marcas'],
};
